import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { RunHistoryFilter } from "../filter/filter";
import { useGetAllRunHistory } from "../../../hooks";


interface RunHistoryData {
  id: string;
  executionDate: string;
  executionTime: string;
  workflowId: string;
  workflowName: string;
  actionType: string;
  audienceType: string;
  recipientCount: number;
  successCount: number;
  failedCount: number;
  channels: string[];
}

interface RunHistoryFilters {
  channel: string;
  actionType: string;
  audienceType: string;
  executionDateFrom: string;
  executionDateTo: string;
}

const RunHistoryList: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read all params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const channel = searchParams.get("channel") || "";
  const actionType = searchParams.get("actionType") || "";
  const audienceType = searchParams.get("audienceType") || "";
  const executionDateFrom = searchParams.get("executionDateFrom") || "";
  const executionDateTo = searchParams.get("executionDateTo") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const {data: runHistoryData} = useGetAllRunHistory(currentPage, PAGE_SIZE, searchQuery, executionDateFrom, executionDateTo, channel, audienceType, actionType)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const channelList = [
    { label: "Email", value: "email" },
    { label: "SMS", value: "sms" },
    { label: "Push Notification", value: "push_notification" },
    { label: "WhatsApp", value: "whatsapp" },
  ];

  const actionTypeList = [
    { label: "Immediate", value: "immediate" },
    { label: "Time-Based", value: "time_based" },
    { label: "Scheduled", value: "scheduled" },
  ];

  const audienceTypeList = [
    { label: "Commercial Customers", value: "commercial" },
    { label: "Residential Customers", value: "residential" },
    { label: "Business Users", value: "business_users" },
    { label: "Field Workers", value: "field_workers" },
  ];


  // Update pagination
  useEffect(() => {
    const totalItems = runHistoryData?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [runHistoryData, currentPage]);

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.channel;
    delete newParams.actionType;
    delete newParams.audienceType;
    delete newParams.executionDateFrom;
    delete newParams.executionDateTo;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(channel && { channel }),
      ...(actionType && { actionType }),
      ...(audienceType && { audienceType }),
      ...(executionDateFrom && { executionDateFrom }),
      ...(executionDateTo && { executionDateTo }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };


  const getPerformanceBadge = (successCount: number, failedCount: number) => {
    const total = successCount + failedCount;
    const successRate = total > 0 ? (successCount / total) * 100 : 0;

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-green-600 font-medium">
            ✓ {successCount}
          </span>
          <span className="text-xs text-red-600 font-medium">
            ✗ {failedCount}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-green-600 h-1.5 rounded-full"
            style={{ width: `${successRate}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const getChannelsBadges = (channels: string[]) => {
    return (
      <div className="flex flex-wrap gap-1">
        {channels?.map((channel, index) => (
          <span
            key={index}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
          >
            {channel}
          </span>
        ))}
      </div>
    );
  };

  const columns: TableColumn<any>[] = [
    {
      key: "completeDate",
      header: "Execution Date",
      sortable: true,
    },
    {
      key: "completedAt",
      header: "Execution Time",
      sortable: true,
    },
    {
      key: "workflow",
      header: "Workflow Name",
      sortable: true,
    },
    {
      key: "actionTypeDisplay",
      header: "Action Type",
      sortable: true,
    },
    {
      key: "audienceTypeDisplay",
      header: "Audience Type",
      sortable: true,
    },
    {
      key: "recipients",
      header: "Recipient Count",
      sortable: true,
      render: (value: number) => (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "performance",
      header: "Performance",
      sortable: false,
      render: (value: any, row: RunHistoryData) =>
        getPerformanceBadge(row?.successCount, row?.failedCount),
    },
    {
      key: "channel",
      header: "Channels",
      sortable: false,
      render: (value: string[]) => getChannelsBadges(value),
    },
  ];

  const actions = [
    {
      label: "View",
      onClick: (row: any) => {
        navigate(`/communication-hub/workflows/view/${row.id}/execution-details`);
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
  ];

  const filterPanel = (
    <RunHistoryFilter
      channel={channel}
      actionType={actionType}
      audienceType={audienceType}
      executionDateFrom={executionDateFrom}
      executionDateTo={executionDateTo}
      channelList={channelList}
      actionTypeList={actionTypeList}
      audienceTypeList={audienceTypeList}
      onApplyFilters={(filters) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          ...(filters.channel && { channel: filters.channel }),
          ...(filters.actionType && { actionType: filters.actionType }),
          ...(filters.audienceType && { audienceType: filters.audienceType }),
          ...(filters.executionDateFrom && {
            executionDateFrom: filters.executionDateFrom,
          }),
          ...(filters.executionDateTo && {
            executionDateTo: filters.executionDateTo,
          }),
        };
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters =
    channel ||
    actionType ||
    audienceType ||
    executionDateFrom ||
    executionDateTo;

  return (
    <div className="space-y-6 p-1">
      <GenericTable
        data={runHistoryData?.results || []}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="icons"
        emptyMessage="No run history found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by workflow name or ID..."
        filterPanel={filterPanel}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {
                  [
                    !!channel,
                    !!actionType,
                    !!audienceType,
                    !!executionDateFrom,
                    !!executionDateTo,
                  ].filter(Boolean).length
                }{" "}
                filter(s) active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default RunHistoryList;
